import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone.js';
import dayjsUtc from 'dayjs/plugin/utc.js';
import { Weekday } from '@biteinc/enums';
dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);
export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;
// // These are listed in order in which they appear in Date.getUTCDay()
export const DAYS = [
    Weekday.Sun,
    Weekday.Mon,
    Weekday.Tue,
    Weekday.Wed,
    Weekday.Thu,
    Weekday.Fri,
    Weekday.Sat,
];
export function areDaysSame(timeA, timeB, timezone) {
    const dayA = dayjs(timeA).tz(timezone);
    const dayB = dayjs(timeB).tz(timezone);
    return (dayA.year() === dayB.year() && dayA.month() === dayB.month() && dayA.date() === dayB.date());
}
export function timestampWithRounding(time, rounding) {
    return Math.round(time / rounding) * rounding;
}
export function roundUnixTimeToNext15(time) {
    const ROUNDING_FACTOR = 15 * MINUTE;
    return Math.ceil(time / ROUNDING_FACTOR) * ROUNDING_FACTOR;
}
export function getStartOfDayFromUnixTime(time, timezone) {
    const local = dayjs(time).tz(timezone);
    const startOfDay = local.startOf('day');
    if (timezone === 'utc') {
        // DayJS does not correctly handle UTC and DST transitions.
        // It incorrectly applies the offset to the value, so we need to adjust it.
        return startOfDay.valueOf() + startOfDay.utcOffset() * MINUTE;
    }
    return startOfDay.valueOf();
}
export function weekTimestampWithRounding(time, timezone, rounding) {
    const roundedTime = timestampWithRounding(time, rounding);
    const local = dayjs(roundedTime).tz(timezone);
    const startOfWeek = local.startOf('week');
    // Get the difference of the offsets to handle DST transitions.
    return (local.valueOf() - startOfWeek.valueOf() + (local.utcOffset() - startOfWeek.utcOffset()) * MINUTE);
}
export function weekTimestamp(time, timezone) {
    return weekTimestampWithRounding(time, timezone, 1);
}
export function timestampRoundedToNearest15Mins(time) {
    return timestampWithRounding(time, MINUTE * 15);
}
export function dayTimestampFromTime(time, timezone) {
    const local = dayjs(time).tz(timezone);
    const startOfDay = local.startOf('day');
    // Get the difference of the offsets to handle DST transitions.
    return (local.valueOf() - startOfDay.valueOf() + (local.utcOffset() - startOfDay.utcOffset()) * MINUTE);
}
export function getFormattedDateFromUnixTime(time, timezone, format) {
    const local = dayjs(time).tz(timezone);
    if (format === 'yyyy-MM-DDTHH:mm:ss') {
        // DayJS does not support lowercase yyyy, so we need to use uppercase YYYY
        return local.format('YYYY-MM-DDTHH:mm:ss');
    }
    return local.format(format);
}
export function getDayFromWeekTimestamp(weekTs) {
    if (weekTs < 0 || WEEK <= weekTs) {
        throw new Error(`Invalid Week Timestamp: ${weekTs}`);
    }
    return DAYS[Math.floor(weekTs / DAY)];
}
export function getWeekdayFromUnixTime(time, timezone) {
    // dayjs().day() returns 0 (Sunday) to 6 (Saturday)
    const dayNumber = dayjs(time).tz(timezone).day();
    return DAYS[dayNumber];
}
export function getDayTimestampFromWeekTimestamp(weekTs) {
    if (weekTs < 0 || WEEK <= weekTs) {
        throw new Error(`Invalid Week Timestamp: ${weekTs}`);
    }
    return weekTs % DAY;
}
export function hourRangeFromSchedule(schedule) {
    const { from, to } = schedule;
    return {
        from: from.hours * HOUR + from.minutes * MINUTE,
        to: to.hours * HOUR + to.minutes * MINUTE,
    };
}
export function dayTimestampFromScheduleBoundary(scheduleBoundary) {
    return HOUR * scheduleBoundary.hours + MINUTE * scheduleBoundary.minutes;
}
export function getDayAdjustedRanges(data, wrapSaturday = false) {
    const periods = [];
    let satPeriod;
    DAYS.forEach((day) => {
        const dayData = data[day];
        if (dayData) {
            const from = dayData.from + DAYS.indexOf(day) * DAY;
            let to = dayData.to + DAYS.indexOf(day) * DAY;
            if (dayData.to <= dayData.from) {
                to += DAY;
            }
            const period = { from, to };
            periods.push(period);
            if (day === Weekday.Sat) {
                satPeriod = period;
            }
        }
    });
    // Often the Sun period starts at 4am which is when the Sat one ends.
    // In that case, the first 4 hours are unaccounted for since the Sat period
    // is at the end.
    if (wrapSaturday && satPeriod && satPeriod.from < WEEK && satPeriod.to > WEEK) {
        periods.splice(0, 0, { from: 0, to: satPeriod.to - WEEK });
    }
    return periods;
}
export function rangeThatIsActiveAt(rangeByWeekday, weekTs) {
    return getDayAdjustedRanges(rangeByWeekday, true).find((period) => {
        return period.from <= weekTs && weekTs < period.to;
    });
}
export function scheduleIsActiveAt(schedule, dayTs) {
    if (!(0 < dayTs || dayTs < DAY)) {
        throw new Error(`Invalid Day Timestamp: ${dayTs}`);
    }
    const range = hourRangeFromSchedule(schedule);
    // If to is before from, then wrap around by adding a day
    const trueTo = range.from < range.to ? range.to : range.to + DAY;
    return range.from <= dayTs && dayTs < trueTo;
}
//# sourceMappingURL=universal_time_helper.js.map